/** Shared segmented-toggle state adapter. */
import React, { memo, useMemo } from "react";

import ViewModeToggleView, {
  type PreparedViewModeToggleItem,
} from "./ViewModeToggleView";

/** Minimal DOM keyboard shape forwarded by React Native Web. */
interface WebTabKeyboardEvent {
  readonly key: string;
  readonly currentTarget?: {
    readonly parentElement?: {
      querySelectorAll?: (selector: string) => {
        readonly length: number;
        readonly [index: number]: { focus?: () => void } | undefined;
      };
    };
  };
  preventDefault: () => void;
}

/**
 * Resolve the next tab index for the WAI-ARIA tabs keyboard contract.
 * @param key - Pressed keyboard key.
 * @param index - Current tab index.
 * @param length - Total number of tabs.
 * @returns Destination index, or null for an unrelated key.
 */
const nextTabIndex = (key: string, index: number, length: number) => {
  if (key === "ArrowRight") return (index + 1) % length;
  if (key === "ArrowLeft") return (index - 1 + length) % length;
  if (key === "Home") return 0;
  if (key === "End") return length - 1;
  return null;
};

/** One selectable representation in the toggle. */
export interface ViewModeToggleItem<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon: React.ElementType;
  readonly testID: string;
}

/** Props for the shared segmented view toggle. */
export interface ViewModeToggleProps<T extends string> {
  readonly value: T;
  readonly items: readonly ViewModeToggleItem<T>[];
  readonly onValueChange: (value: T) => void;
  readonly accessibilityLabel?: string;
}

/**
 * Prepares stable segment callbacks and accessibility state for the pure view.
 * @param root0 - Toggle props.
 * @param root0.value - Selected mode.
 * @param root0.items - Available modes.
 * @param root0.onValueChange - Selection callback.
 * @param root0.accessibilityLabel - Accessible group name.
 */
const ViewModeToggleContainer = <T extends string>({
  value,
  items,
  onValueChange,
  accessibilityLabel = "View mode",
}: ViewModeToggleProps<T>) => {
  const preparedItems = useMemo<readonly PreparedViewModeToggleItem[]>(
    () =>
      items.map((item, index) => ({
        ...item,
        isActive: item.value === value,
        accessibilityState: { selected: item.value === value },
        onPress: () => onValueChange(item.value),
        onKeyDown: (event: WebTabKeyboardEvent) => {
          const targetIndex = nextTabIndex(event.key, index, items.length);
          if (targetIndex === null) return;
          const tabs =
            event.currentTarget?.parentElement?.querySelectorAll?.(
              '[role="tab"]'
            );
          event.preventDefault();
          onValueChange(items[targetIndex].value);
          tabs?.[targetIndex]?.focus?.();
        },
      })),
    [items, onValueChange, value]
  );

  return (
    <ViewModeToggleView
      items={preparedItems}
      accessibilityLabel={accessibilityLabel}
    />
  );
};

ViewModeToggleContainer.displayName = "ViewModeToggleContainer";
export default memo(ViewModeToggleContainer) as typeof ViewModeToggleContainer;
